
import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { DynamicForm } from "./DynamicForm";
import { FormField } from "./FormData";
import { FormFieldType } from "./FormDefinition";
import { Dictionary } from "../datastructures/Dictionary";

const COLUMN_WIDTHS = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12"
];

@Component<DynamicFormBuilder>({
  components: {}
})
export default class DynamicFormBuilder extends Vue {
  @Prop({
    default() {
      return new DynamicForm({});
    }
  })
  protected readonly form!: DynamicForm;
  @Prop({ type: Boolean, default: false })
  protected readonly responsive!: boolean;
  @Prop({ type: Boolean, default: false })
  protected readonly emitOnInit!: boolean;
  @Prop({ type: Boolean, default: false })
  protected readonly hideDetails!: boolean;
  @Prop({ type: Boolean, default: false })
  protected readonly persistentHint!: boolean;
  @Prop({ type: String, default: undefined })
  protected readonly checkboxColor?: string;
  @Prop({ type: String, default: "12" })
  protected readonly appendWidth!: string;
  @Prop({ type: String, default: undefined })
  protected readonly appendLgWidth?: string;

  @Watch("form")
  protected onFormChanged() {
    this.init();
  }

  protected get fields() {
    return Object.values(this.form.fields).filter(field => !field.hidden);
  }

  protected get hasAppendFormSlot() {
    return !!this.$slots["append-form"] || !!this.$scopedSlots["append-form"];
  }

  protected get trailingColumnProps() {
    return this.buildColumnProps(this.appendWidth, this.appendLgWidth);
  }

  protected columnProps(field: FormField) {
    return this.buildColumnProps(field.width, field.lgWidth);
  }

  protected hasAppendSlot(field: FormField) {
    return !!this.$scopedSlots[`append-${field.key}`];
  }

  protected isMultiple(field: FormField) {
    return !!field.multiple || field.type === FormFieldType.MultiSelect;
  }

  private buildColumnProps(width?: string, lgWidth?: string) {
    const props: Dictionary<boolean> = { xs12: true };
    const smWidth = this.responsive ? "6" : width ?? "12";

    if (COLUMN_WIDTHS.includes(smWidth)) {
      props[`sm${smWidth}`] = true;
    }

    if (lgWidth && COLUMN_WIDTHS.includes(lgWidth)) {
      props[`lg${lgWidth}`] = true;
    }

    return props;
  }

  public validate() {
    this.emitValidate();
  }

  protected isTextfield(field: FormField) {
    return field.type === FormFieldType.Text && !field.multiline;
  }

  protected isTextareafield(field: FormField) {
    return field.type === FormFieldType.Text && field.multiline;
  }

  protected isNumberfield(field: FormField) {
    return field.type === FormFieldType.Number;
  }

  protected isCheckbox(field: FormField) {
    return field.type === FormFieldType.Checkbox;
  }

  protected isDatefield(field: FormField) {
    return field.type === FormFieldType.Date;
  }

  protected isSelect(field: FormField) {
    return (
      field.type === FormFieldType.Selection ||
      field.type === FormFieldType.MultiSelect
    );
  }

  protected isInterval(field: FormField) {
    return field.type === FormFieldType.Interval;
  }

  protected setFieldValue(fieldName: string, value: unknown) {
    this.form.setFieldValue(fieldName, value);
    this.emitChange(fieldName, value);
  }

  protected emitChange(fieldName?: string, value?: unknown) {
    const data = this.form.getData();
    this.$emit("change", data, fieldName, value);

    if (this.form.isValid()) {
      this.$emit("change:valid", data, fieldName, value);
    }
  }

  protected emitValidate() {
    this.$emit("validate", this.form.isValid());
  }

  protected mounted() {
    this.init();
  }

  protected init() {
    this.form.init();

    if (this.emitOnInit) {
      this.emitChange();
    }
  }
}
